import { Injectable, NgZone, OnDestroy } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export interface PerfStats {
  timestamp: number;
  memoryMB: number | null; // Used JS heap in MB (if available)
  memoryLimitMB: number | null; // Total JS heap limit in MB (if available)
  memoryPct: number | null; // Percentage of heap used (if available)
  cpuLoadPct: number; // Heuristic 0-100 based on event loop lag + long tasks
  fps: number; // Frames per second estimate
  eventLoopLagMs: number; // Latest event loop lag (ms)
  longTaskMs: number; // Long tasks time within last window (ms)
}

@Injectable({ providedIn: 'root' })
export class PerfMonitorService implements OnDestroy {
  private stats$ = new BehaviorSubject<PerfStats>({
    timestamp: Date.now(),
    memoryMB: null,
    memoryLimitMB: null,
    memoryPct: null,
    cpuLoadPct: 0,
    fps: 60,
    eventLoopLagMs: 0,
    longTaskMs: 0,
  });

  private intervalId: any = null;
  private rafId: number | null = null;
  private frames = 0;
  private lastFpsTs = performance.now();
  private lastTick = performance.now();
  private longTaskObserver: PerformanceObserver | null = null;
  private longTaskAccumMs = 0;
  private running = false;

  constructor(private zone: NgZone) {}

  start(sampleMs = 1000): void {
    if (this.running) return;
    this.running = true;
    this.longTaskAccumMs = 0;
    this.lastTick = performance.now();
    this.frames = 0;
    this.lastFpsTs = performance.now();

    // Long Tasks API (if available)
    if ('PerformanceObserver' in window && (PerformanceObserver as any).supportedEntryTypes?.includes('longtask')) {
      try {
        this.longTaskObserver = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            // @ts-ignore
            const dur = (entry.duration as number) || 0;
            this.longTaskAccumMs += dur;
          }
        });
        this.longTaskObserver.observe({ type: 'longtask', buffered: true } as any);
      } catch {
        this.longTaskObserver = null;
      }
    }

    // FPS via rAF
    const runRaf = () => {
      this.frames++;
      this.rafId = requestAnimationFrame(runRaf);
    };

    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame(runRaf);
      this.intervalId = setInterval(() => this.sample(sampleMs), sampleMs);
    });
  }

  stop(): void {
    this.running = false;
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
    if (this.longTaskObserver) {
      try { this.longTaskObserver.disconnect(); } catch {}
      this.longTaskObserver = null;
    }
  }

  getStats$(): Observable<PerfStats> {
    return this.stats$.asObservable();
  }

  // Obtener memoria actual (MB) si está disponible en el navegador
  getCurrentMemoryMB(): number | null {
    const mem: any = (performance as any).memory;
    if (mem && typeof mem.usedJSHeapSize === 'number') {
      return mem.usedJSHeapSize / (1024 * 1024);
    }
    return null;
  }

  // Snapshot rápido de estado actual
  getSnapshot(): PerfStats {
    return this.stats$.value;
  }

  private sample(windowMs: number): void {
    const now = performance.now();

    // Event loop lag
    const expected = this.lastTick + windowMs;
    const lag = Math.max(0, now - expected);
    this.lastTick = now;

    // FPS calculation
    const elapsed = now - this.lastFpsTs;
    let fps = this.stats$.value.fps;
    if (elapsed >= 500) {
      fps = Math.round((this.frames / elapsed) * 1000);
      this.frames = 0;
      this.lastFpsTs = now;
    }

    // Long tasks accumulated in this window
    const longTaskMs = this.longTaskAccumMs;
    this.longTaskAccumMs = 0; // reset for next window

    // Memory (Chrome/Edge). Safari: not available
    let memoryMB: number | null = null;
    let memoryLimitMB: number | null = null;
    let memoryPct: number | null = null;
    const mem: any = (performance as any).memory;
    if (mem && typeof mem.usedJSHeapSize === 'number' && typeof mem.jsHeapSizeLimit === 'number') {
      memoryMB = mem.usedJSHeapSize / (1024 * 1024);
      memoryLimitMB = mem.jsHeapSizeLimit / (1024 * 1024);
      memoryPct = Math.max(0, Math.min(100, (memoryMB / Math.max(1, memoryLimitMB)) * 100));
    }

    // CPU load heuristic [0-100]: combine long tasks ratio and normalized lag
    const longTaskRatio = Math.max(0, Math.min(1, longTaskMs / windowMs));
    const lagRatio = Math.max(0, Math.min(1, lag / windowMs));
    // Weight long tasks more than lag; also factor FPS droop
    const fpsPenalty = Math.max(0, Math.min(1, (60 - Math.min(60, fps)) / 60));
    const cpuLoadPct = Math.round((longTaskRatio * 0.6 + lagRatio * 0.3 + fpsPenalty * 0.1) * 100);

    this.stats$.next({
      timestamp: Date.now(),
      memoryMB,
      memoryLimitMB,
      memoryPct,
      cpuLoadPct,
      fps,
      eventLoopLagMs: Math.round(lag),
      longTaskMs: Math.round(longTaskMs),
    });
  }

  ngOnDestroy(): void {
    this.stop();
  }
}
